<script lang="ts">
  interface Props {
    open: boolean;
    unread?: number;
    label: string;
    onclick: () => void;
    shape?: 'circle' | 'rounded-square' | 'pill';
    size?: 'sm' | 'md' | 'lg';
    animation?: 'none' | 'pulse' | 'bounce' | 'breath' | 'wave';
    imageUrl?: string;
  }

  let {
    open,
    unread = 0,
    label,
    onclick,
    shape = 'circle',
    size = 'md',
    animation = 'none',
    imageUrl,
  }: Props = $props();

  // Flip when an <img> fails to load so we fall back to the SVG icon.
  let imageBroken = $state(false);

  // Reset the broken flag whenever the URL changes so the user can retry.
  $effect(() => {
    void imageUrl;
    imageBroken = false;
  });

  const useImage = $derived(!!imageUrl && imageUrl.length > 0 && !imageBroken);
</script>

<button
  type="button"
  class="hd-fab hd-fab--size-{size} hd-fab--shape-{shape} hd-fab--anim-{animation}"
  class:hd-fab--open={open}
  class:hd-fab--has-image={useImage}
  aria-label={label}
  aria-expanded={open}
  aria-haspopup="dialog"
  {onclick}
>
  {#if useImage && !open}
    <img
      class="hd-fab__img"
      src={imageUrl}
      alt=""
      aria-hidden="true"
      onerror={() => (imageBroken = true)}
    />
  {:else}
    <span class="hd-fab__icons" aria-hidden="true">
      <svg class="hd-fab__icon hd-fab__icon--chat" viewBox="0 0 24 24">
        <path
          d="M20 2H4a2 2 0 0 0-2 2v14l4-4h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2Z"
          fill="currentColor"
        />
      </svg>
      <svg class="hd-fab__icon hd-fab__icon--close" viewBox="0 0 24 24">
        <path
          d="M6 6l12 12M18 6L6 18"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </span>
  {/if}
  {#if !open && unread > 0}
    <span class="hd-fab__badge" aria-label={`${unread} unread`}>{unread > 9 ? '9+' : unread}</span>
  {/if}
</button>

<style>
  .hd-fab {
    position: relative;
    border: none;
    cursor: pointer;
    background: var(--hd-primary, #2563eb);
    color: var(--hd-primary-fg, #ffffff);
    box-shadow:
      0 8px 24px -6px rgb(0 0 0 / 0.22),
      0 2px 6px -1px rgb(0 0 0 / 0.1),
      inset 0 0 0 1px rgb(255 255 255 / 0.12);
    display: grid;
    place-items: center;
    overflow: hidden;
    transition:
      transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
      box-shadow 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
      background-color 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
      color 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  /* Size */
  .hd-fab--size-sm { width: 48px; height: 48px; }
  .hd-fab--size-md { width: 56px; height: 56px; }
  .hd-fab--size-lg { width: 68px; height: 68px; }
  /* Shape */
  .hd-fab--shape-circle { border-radius: 9999px; }
  .hd-fab--shape-rounded-square { border-radius: 16px; }
  .hd-fab--shape-pill { border-radius: 9999px; }
  /* Pill: slightly wider than tall — ~1.15x of the size */
  .hd-fab--shape-pill.hd-fab--size-sm { width: 55px; }
  .hd-fab--shape-pill.hd-fab--size-md { width: 64px; }
  .hd-fab--shape-pill.hd-fab--size-lg { width: 78px; }

  .hd-fab:hover {
    transform: translateY(-2px);
    box-shadow:
      0 12px 28px -6px rgb(0 0 0 / 0.25),
      0 4px 10px -2px rgb(0 0 0 / 0.12),
      inset 0 0 0 1px rgb(255 255 255 / 0.12);
  }
  .hd-fab:active {
    transform: translateY(0) scale(0.96);
    transition-duration: 100ms;
  }
  .hd-fab:focus-visible {
    outline: 2px solid var(--hd-primary, #2563eb);
    outline-offset: 3px;
  }
  .hd-fab--open {
    background: var(--hd-surface, #ffffff);
    color: var(--hd-fg, #0f172a);
    box-shadow:
      0 8px 24px -6px rgb(0 0 0 / 0.18),
      0 2px 6px -1px rgb(0 0 0 / 0.08),
      inset 0 0 0 1px rgb(15 23 42 / 0.06);
  }
  .hd-fab__icons {
    position: relative;
    width: 22px;
    height: 22px;
    display: block;
  }
  .hd-fab__icon {
    position: absolute;
    inset: 0;
    width: 22px;
    height: 22px;
    transition:
      transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
      opacity 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  .hd-fab__icon--chat {
    opacity: 1;
    transform: rotate(0deg) scale(1);
  }
  .hd-fab__icon--close {
    opacity: 0;
    transform: rotate(-90deg) scale(0.8);
  }
  .hd-fab--open .hd-fab__icon--chat {
    opacity: 0;
    transform: rotate(90deg) scale(0.8);
  }
  .hd-fab--open .hd-fab__icon--close {
    opacity: 1;
    transform: rotate(0deg) scale(1);
  }
  /* Image slot — fills the button and clips to its shape */
  .hd-fab__img {
    width: calc(100% - 12px);
    height: calc(100% - 12px);
    object-fit: cover;
    border-radius: inherit;
    pointer-events: none;
    user-select: none;
    display: block;
  }
  .hd-fab--size-lg .hd-fab__img {
    width: calc(100% - 16px);
    height: calc(100% - 16px);
  }
  .hd-fab__badge {
    position: absolute;
    top: -2px;
    right: -2px;
    min-width: 20px;
    height: 20px;
    padding: 0 5px;
    border-radius: 9999px;
    background: #ef4444;
    color: #ffffff;
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    display: grid;
    place-items: center;
    box-shadow:
      0 0 0 2px var(--hd-surface, #ffffff),
      0 1px 2px rgb(0 0 0 / 0.18);
  }

  /* ---- Animations ------------------------------------------------------- */

  /* pulse — continuous subtle scale + shadow breath */
  .hd-fab--anim-pulse { animation: hd-pulse 2.4s ease-in-out infinite; }
  @keyframes hd-pulse {
    0%, 100% {
      transform: scale(1);
      box-shadow:
        0 8px 24px -6px rgb(0 0 0 / 0.22),
        0 2px 6px -1px rgb(0 0 0 / 0.10);
    }
    50% {
      transform: scale(1.03);
      box-shadow:
        0 10px 30px -4px rgb(0 0 0 / 0.28),
        0 4px 8px -2px rgb(0 0 0 / 0.14);
    }
  }

  /* bounce — small bounce every 5s */
  .hd-fab--anim-bounce { animation: hd-bounce-fab 5s cubic-bezier(0.28, 0.84, 0.42, 1) infinite; }
  @keyframes hd-bounce-fab {
    0%, 88%, 100% { transform: translateY(0); }
    90% { transform: translateY(-6px); }
    94% { transform: translateY(-3px); }
    98% { transform: translateY(0); }
  }

  /* breath — slow opacity/scale + brightness */
  .hd-fab--anim-breath { animation: hd-breath 3.4s ease-in-out infinite; }
  @keyframes hd-breath {
    0%, 100% { transform: scale(1); filter: brightness(1); }
    50% { transform: scale(1.015); filter: brightness(1.06); }
  }

  /* wave — sheen sweep */
  .hd-fab--anim-wave::before {
    content: '';
    position: absolute;
    top: 0;
    left: -60%;
    width: 60%;
    height: 100%;
    background: linear-gradient(
      120deg,
      transparent 0%,
      rgba(255, 255, 255, 0.35) 50%,
      transparent 100%
    );
    animation: hd-wave 4s ease-in-out infinite;
    pointer-events: none;
  }
  @keyframes hd-wave {
    0%, 60% { left: -60%; }
    80% { left: 120%; }
    100% { left: 120%; }
  }

  /* Reduced motion — users opted out of animations */
  @media (prefers-reduced-motion: reduce) {
    .hd-fab,
    .hd-fab__icon { transition: none; }
    .hd-fab--anim-pulse,
    .hd-fab--anim-bounce,
    .hd-fab--anim-breath,
    .hd-fab--anim-wave { animation: none; }
    .hd-fab--anim-wave::before { display: none; }
  }

  /* Stop animations while the panel is open — avoids visual noise. */
  .hd-fab--open.hd-fab--anim-pulse,
  .hd-fab--open.hd-fab--anim-bounce,
  .hd-fab--open.hd-fab--anim-breath { animation: none; }
  .hd-fab--open.hd-fab--anim-wave::before { display: none; }
</style>
